import { create } from 'zustand';
import { User, Role } from '../types/user';
import * as db from '../lib/firebase/database';

type TeamState = {
  users: Record<string, User>;
  roles: Record<string, Role>;
  loading: boolean;
  error: string | null;
  filters: {
    search: string;
    role: string;
    gender: string;
    status: string;
  };
  fetchUsers: () => Promise<void>;
  fetchRoles: () => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
  updateUser: (userId: string, updates: Partial<User>) => Promise<void>;
  createRole: (role: Role) => Promise<void>;
  updateRole: (roleId: string, updates: Partial<Role>) => Promise<void>;
  deleteRole: (roleId: string) => Promise<void>;
  setFilters: (filters: Partial<TeamState['filters']>) => void;
  initialized: boolean;
  initialize: () => Promise<void>;
};

export const useTeamStore = create<TeamState>((set, get) => ({
  users: {},
  roles: {},
  loading: false,
  initialized: false,
  error: null,
  filters: {
    search: '',
    role: '',
    gender: '',
    status: ''
  },

  fetchUsers: async () => {
    console.log('TeamStore - Fetching users');
    set({ loading: true, error: null });
    
    try {
      const users = await db.getAllUsers();
      set((state) => ({ 
        users,
        loading: false 
      }));
      console.log('TeamStore - Users fetched successfully');
    } catch (error: any) {
      console.error('TeamStore - Error fetching users:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchRoles: async () => {
    console.log('TeamStore - Fetching roles');
    set({ loading: true, error: null });
    
    try {
      const roles = await db.getAllRoles();
      set((state) => ({ 
        roles,
        loading: false 
      }));
      console.log('TeamStore - Roles fetched successfully');
    } catch (error: any) {
      console.error('TeamStore - Error fetching roles:', error);
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteUser: async (userId: string) => {
    set({ loading: true, error: null });
    try {
      await db.deleteUserProfile(userId);
      set(state => {
        const newUsers = { ...state.users };
        delete newUsers[userId];
        return { users: newUsers, loading: false };
      });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateUser: async (userId: string, updates: Partial<User>) => {
    set({ loading: true, error: null });
    try {
      await db.updateUserProfile(userId, updates);
      set(state => ({
        users: {
          ...state.users,
          [userId]: { ...state.users[userId], ...updates }
        },
        loading: false
      }));
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createRole: async (role: Role) => {
    set({ loading: true, error: null });
    try {
      await db.createRole(role);
      set(state => ({
        roles: {
          ...state.roles,
          [role.id]: role
        },
        loading: false
      }));
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateRole: async (roleId: string, updates: Partial<Role>) => {
    set({ loading: true, error: null });
    try {
      await db.updateRole(roleId, updates);
      set(state => ({
        roles: {
          ...state.roles,
          [roleId]: { ...state.roles[roleId], ...updates }
        },
        loading: false
      }));
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteRole: async (roleId: string) => {
    set({ loading: true, error: null });
    try {
      await db.deleteRole(roleId);
      set(state => {
        const newRoles = { ...state.roles };
        delete newRoles[roleId];
        return { roles: newRoles, loading: false };
      });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters }
    }));
  }
}));